import dynamic from "next/dynamic";
import cls from "classnames";
import { useState, useEffect, memo, useMemo } from "react";
import toast from "react-hot-toast";
import * as Sentry from "@sentry/nextjs";

import ThankyouModal from "./modals/ThankyouModal";
import AppStoreModal from "./modals/AppStoreModal";
import AddMobileModal from "./modals/login/AddMobileModal";
import SubscribeModalWrapper from "./modals/SubscribeModalWrapper";
import JoinTelegramModal from "./modals/JoinTelegramModal";
import ProfileUpdateForm from "../auth/LoginModal/ProfileUpdateForm";
import PaymentDisabledModal from "./modals/PaymentDisabledModal";
import WhatsappThankYouModal from "./modals/WhatsappThankYouModal";
import MeetingDetailsModal from "./modals/MeetingDetailsModal";

import Modal from "@qoohoo/common/library/modals/Modal";
import useSessionStore from "../../store/sessionStore";
import useStore from "../../store/store";
import Spinner from "@qoohoo/common/library/loader/Spinner";
import { SHOW_PROFILE_SETUP } from "../../constants/constance";
import ModalWrapper from "./modals/login/ModalWrapper";
import { openPayment } from "../../library/payment";
import httpf from "../../utils/frontend/httpf";
import * as Track from "@qoohoo/common/utils/analytics/track";
import { addTagsInRecording } from "@qoohoo/common/utils/analytics/clarity";
import OneToOneThankYouModal from "./modals/OneToOneThankYouModal";
import PaymentConsentTermsModal from "./modals/PaymentConsentTermsModal";
import {getCustomUserForm} from "../../library/helpers/customConfigs/userFormsConfig";
import CustomUserFormModal from "./modals/CustomUserFormModal";
import { openInNewTab } from "@qoohoo/common/utils/url";

const SubscriptionBundleModal = dynamic(() =>
  import("./modals/SubscriptionBundleModal")
);

const JoinModals = ({
                      modalOpen,
                      setModalOpen,
                      channelDetails,
                      isSubscribed,
                      isPrivate,
                      isCreator = false,
                      isCoCreator = false,
                      isPaymentRequired = true,
                      isLoadingApiResponse = false,
                      showAppDownloadModal=false,
                      userChannelDetail,
                      getUserChannelDetail,
                      setIsLoading=()=>{},
                      couponState,
                      prepareCouponResponse,
                      onCouponValidation,
                      isWhiteTheme,
                      settings={},
                      customConfig={},
                      showTaxDetails=false
                    }) => {
  const userData = useSessionStore((state) => state.userData);

  const [showAuthModal, setShowAuthModal] = useState(false);

  const [hasAcceptedPaymentConsentTerms, setHasAcceptedPaymentConsentTerms] = useState(false);
  const [hasSubmittedCustomUserForm, setHasSubmittedCustomUserForm] = useState(isSubscribed ? true : false);

  const [orderPaid, setOrderPaid] = useState(false);
  const [isDoingPayment, setIsDoingPayment] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(
    "Checking your seats..."
  );

  const [showThanksModal, setShowThanksModal] = useState(false);
  const [hideTitleImage, setHideTitleImage] = useState(false);
  const [isModalCloseAllowed, setIsModalCloseAllowed] = useState(true);

  const [bookingUid, setBookingUid] = useState(null);

  const readyToPay = useStore((state) => state.readyToPay);
  const setReadyToPay = useStore((state) => state.setReadyToPay);

  const showProfileUpdateModal = useMemo(() => {
      return SHOW_PROFILE_SETUP && !!userData?.uid && readyToPay && !userData?.is_basic_profile_setup_complete;
    }
    ,[userData?.uid, readyToPay, userData?.is_basic_profile_setup_complete]);

  const showMeetingDetailsModal = useStore(
    (state) => state.showMeetingDetailsModal
  );
  const showOnboardingModal = useStore((state) => state.showOnboardingModal);
  const setShowOnboardingModal = useStore(
    (state) => state.setShowOnboardingModal
  );
  const selectedPlanUid = useStore((state) => state.selectedPlanUid);
  const isAuthFailed = useStore((state) => state.isAuthFailed);
  const setIsAuthFailed = useStore((state) => state.setIsAuthFailed);
  const setAuthFailedModalOpenState = useStore(
    (state) => state.setAuthFailedModalOpenState
  );
  // TODO:
  // isSubscribed = true;
  const isTelegramChannel = channelDetails.is_telegram;
  const showTelegramModal = isTelegramChannel && orderPaid;
  const isPaidChannel = !!channelDetails?.is_paid;
  const hasSubscriptionPlans = channelDetails.plans?.length > 1;
  const showSubscriptionBundleModal = hasSubscriptionPlans && !orderPaid;
  const isWhatsappChannel = channelDetails?.is_whatsapp;
  const isPaymentActiveForWhatsappChannel =
    channelDetails?.whatsapp?.is_payment_active;
  const showWhatsappModal = isWhatsappChannel && orderPaid;
  const isOneToOne = channelDetails?.type?.toLowerCase() === "one-to-one";
  const trackDetails = {};

  const customSettings = {
    ...settings,
    ...customConfig
  }

  const paymentConsentTerms = useMemo(() => userChannelDetail?.payment_terms, [userChannelDetail]);
  const customUserFormConfig = useMemo(() => isOneToOne ? null : getCustomUserForm(channelDetails?.uid, channelDetails?.creator.uid), [channelDetails]);

  const showPaymentConsentTermsModal = useMemo(()=>{
    const hasUserChannelDetails = Object.keys(userChannelDetail).length > 0;

    return !hasAcceptedPaymentConsentTerms && !!userData?.uid && readyToPay && paymentConsentTerms && hasUserChannelDetails;
  }, [hasAcceptedPaymentConsentTerms, userData?.uid,  paymentConsentTerms, readyToPay, userChannelDetail]);

  const showCustomUserFormModal = useMemo(()=>{
    const hasUserChannelDetails = Object.keys(userChannelDetail).length > 0;

    return !hasSubmittedCustomUserForm && !!userData?.uid && readyToPay && customUserFormConfig?.api && hasUserChannelDetails;
  }, [hasSubmittedCustomUserForm, userData?.uid,  customUserFormConfig, readyToPay, userChannelDetail]);

  const isRecurring = channelDetails?.type?.toLowerCase() === "recurring";

  const isOneTimePlan = channelDetails?.plans?.length === 0;
  const hasMultiplePlans = channelDetails?.plans?.length > 1;

  const firstChannelPlan = channelDetails?.plans?.[0];

  let channelFee;

  if (isOneTimePlan || isOneToOne) {
    channelFee = channelDetails?.channel_fee;
  } else {
    channelFee = firstChannelPlan?.amount_after_discount || firstChannelPlan?.amount;
  }

  const acceptDisclaimer = () => {
    setHasAcceptedPaymentConsentTerms(true);
  }

  const onSubmitCustomUserForm = () => {
    setHasSubmittedCustomUserForm(true);
  }

  function disableCloseModal() {
    setIsModalCloseAllowed(false);
  }

  function closeModal() {
    if (showThanksModal && !isModalCloseAllowed) return;

    if (showThanksModal) {
      setShowThanksModal(false);
    }

    if(hasAcceptedPaymentConsentTerms) {
      setHasAcceptedPaymentConsentTerms(false);
    }

    setModalOpen(false);
    setOrderPaid(false);
    setReadyToPay(false);
  }

  useEffect(() => {
    if (isAuthFailed) {
      closeModal();
    }
  }, [isAuthFailed]);

  const afterSubscribeModalLeave = () => {
    if (isAuthFailed) {
      setIsAuthFailed(false);
      setAuthFailedModalOpenState(true);
    }
  };

  async function updatePaymentStatusInCustomUserForm() {
    if (customUserFormConfig.api) {
      if (customUserFormConfig.api.update_on_payment.method === "POST") {
        const payload =
          customUserFormConfig.api.update_on_payment.preparePayload(
            userData,
            channelDetails
          );

        await httpf.post(
          customUserFormConfig.api.update_on_payment.url,
          payload
        );
      } else {
        // TODO: Handle GET later
      }
    }
  }

  function getThankYouModal() {
    let thankYouModal = null;

    if (isTelegramChannel) {
      thankYouModal = (
        <JoinTelegramModal
          channelDetails={channelDetails}
          userChannelDetail={userChannelDetail}
          getUserChannelDetail={getUserChannelDetail}
          isLoading={isLoadingApiResponse}
          closeModal={closeModal}
          customSettings={customSettings}
        />
      );
    } else if (isWhatsappChannel) {
      thankYouModal = (
        <WhatsappThankYouModal
          channelDetails={channelDetails}
          userChannelDetail={userChannelDetail}
          getUserChannelDetail={getUserChannelDetail}
          isLoading={isLoadingApiResponse}
          closeModal={closeModal}
          customSettings={customSettings}
        />
      );
    } else if (isOneToOne) {
      // setHideTitleImage(false);
      thankYouModal = (
        <OneToOneThankYouModal
          channelDetails={channelDetails}
          bookingUid={bookingUid}
          disableCloseModal={disableCloseModal}
        />
      );
    } else {
      thankYouModal = (
        <ThankyouModal
          isPrivate={isPrivate}
          channelDetails={channelDetails}
          customSettings={customSettings}
        />
      );
    }

    return thankYouModal;
  }

  function triggerFreePayment() {
    if (userData?.uid)
      httpf
        .post(`/api/subscribe`, { id: channelDetails.uid })
        .then(() => {
          Track.success("payment_modal.result", trackDetails);
          toast.success("Channel joined successfully");
          setModalOpen(true);
          setShowThanksModal(true);
          getUserChannelDetail();
          setOrderPaid(true);
          setReadyToPay(false);
        })
        .catch((err) => {
          Track.error("payment_modal.result", trackDetails);
          console.log(err);
          Sentry.captureException(err);
          toast.error("Error joining channel, please try again!");
        })
  }

  function triggerOneToOnePayment() {
    const couponDiscountedAmount = couponState.data?.amount_after_coupon_discount;
    const amountToPay = couponDiscountedAmount || channelFee;

    const bookingData = {
      uid: channelDetails.uid,
      booking_date: channelDetails.booking_date,
      start_at: channelDetails.start_at,
      guest_emails: channelDetails.guest_emails,
      frontend_amount:  amountToPay,
      platform: channelDetails.platform,
      user_email: channelDetails.user_email,
      user_display_name: channelDetails.user_display_name,
      agenda: channelDetails.agenda,
      coupon: couponState.coupon
    };

    if (userData?.uid)
      httpf
        .post(`/api/meeting/book-slot/?id=${channelDetails.uid}`, bookingData)
        .then((res) => {
          const customData = {
            description: channelDetails.name,
            user_mobile: `+${userData.countryCode}${userData.mobile}`,
            user_email: userData.email,
          };

          openPayment({
            orderId: res.data?.order_id,
            customData,
            closeModal,
            setReadyToPay,
            creatorId: channelDetails?.creator?.uid
          })
            .then(() => {
              setModalOpen(true);
              setOrderPaid(true);
              setShowThanksModal(true);
              Track.success("subscription_bundle_modal.result", trackDetails);
            })
            .catch((err) => {
              console.error(err);
              Sentry.captureException(err);
              toast.error("Payment failed. Please try again");
              Track.error("subscription_bundle_modal.result", trackDetails);
            })
            .finally(() => {
              const bookingUid = res?.data?.booking_uid;
              setBookingUid(bookingUid);
            });
        })
        .catch((err) => {
          const errorMessage = err.message.includes("code")
            ? "Payment failed! Please try again"
            : err.message;

          console.error(err);
          Track.error("subscription_bundle_modal.result", trackDetails);
          Sentry.captureException(err);
          toast.error(errorMessage);
        })
        .finally(() => {
          closeModal();
          setReadyToPay(false);
        });
  }

  function triggerPaidChannelPayment() {
    let planId;

    if (hasMultiplePlans) planId = selectedPlanUid;
    else planId = channelDetails?.plans?.[0]?.uid;

    let coupon = couponState.coupon;
    const isCouponApplicableOnSelectedPlan = couponState.data?.[planId];

    if (isRecurring && !isCouponApplicableOnSelectedPlan){
      coupon = "";
    }

    if (userData?.uid) {
      setIsDoingPayment(true);
      setIsLoading(true);
      setLoadingMessage("Creating order...");

      httpf
        .post("/api/get-order-id", {
          id: channelDetails.uid,
          plan_uid: planId,
          coupon: coupon,
        })
        .then((res) => {

          const customData = {
            description: channelDetails.name,
            category: channelDetails.category[0].name,
            user_mobile: `+${userData.countryCode}${userData.mobile}`,
            user_email: userData.email,
          };

          openPayment({
            orderId: res.data?.order_id,
            customData,
            closeModal,
            setReadyToPay,
            setIsLoading,
            setIsDoingPayment,
            setLoadingMessage,
            creatorId: channelDetails?.creator?.uid
          })
            .then(() => {
              setModalOpen(true);
              setOrderPaid(true);
              customUserFormConfig && updatePaymentStatusInCustomUserForm();
              setShowThanksModal(true);
              Track.success("subscription_bundle_modal.result", trackDetails);
            })
            .catch((err) => {
              console.error(err);
              Sentry.captureException(err);
              toast.error("Payment failed. Please try again");
              Track.error("subscription_bundle_modal.result", trackDetails);
            })
            .finally(() => {
              setIsLoading(false);
              setIsDoingPayment(false);
              getUserChannelDetail();
            });
        })
        .catch((err) => {
          setIsDoingPayment(false);
          setIsLoading(false);
          closeModal();

          const errorMessage = err.message.includes("code")
            ? "Payment failed! Please try again"
            : err.message;

          console.error(err);
          Track.error("subscription_bundle_modal.result", trackDetails);
          Sentry.captureException(err);
          toast.error(errorMessage);
        })
        .finally(() => {
          setReadyToPay(false);
        });
    }
  }

  function triggerPaidPayment() {
    const isOneToOne = channelDetails?.type?.toLowerCase() === "one-to-one";

    if (isOneToOne) triggerOneToOnePayment();
    else triggerPaidChannelPayment();
  }

  function triggerPayment() {
    if (isPaidChannel) {
      triggerPaidPayment();
    } else {
      triggerFreePayment();
    }
  }

  useEffect(() => {
    // to make sure isSubscribed, showExtendMembershipButton are reliable
    const hasUserChannelDetails = Object.keys(userChannelDetail).length > 0;

    const isLoggedIn = !!userData?.uid;

    if (showProfileUpdateModal) {
      return; //Do not initiate payment until basic setup is complete.
    }

    if(!hasAcceptedPaymentConsentTerms && paymentConsentTerms) {
      return; //Do not initiate payment until disclaimer is accepted.
    }

    if(!hasSubmittedCustomUserForm && customUserFormConfig?.api) {
      return; //Do not initiate payment until disclaimer is accepted.
    }

    if (isLoggedIn && readyToPay && !orderPaid && hasUserChannelDetails) {
      triggerPayment();
    }

    return () => {
      if(orderPaid) {
        setReadyToPay(false);
      }
    };
  }, [readyToPay, userData?.uid, showProfileUpdateModal, hasAcceptedPaymentConsentTerms, paymentConsentTerms, hasSubmittedCustomUserForm, customUserFormConfig, orderPaid, userChannelDetail]);

  function handleModalShowing() {
    if (showProfileUpdateModal) {
      return (
        <ProfileUpdateForm
          closeModal={closeModal}
          closeOnSuccess={false}
          customSettings={customSettings}
        />
      );
    }

    if(showPaymentConsentTermsModal) {
      return (
        <PaymentConsentTermsModal
          onClose={closeModal}
          onAccept={acceptDisclaimer}
          paymentConsentTerms={paymentConsentTerms}
          channelDetails={channelDetails}
        />
      )
    }

    if(showCustomUserFormModal) {
      return (
        <CustomUserFormModal
          onClose={closeModal}
          onSubmitCustomUserForm={onSubmitCustomUserForm}
          formConfig={customUserFormConfig}
          channelDetails={channelDetails}
        />
      )
    }

    if (!showAuthModal) {
      if (isWhatsappChannel && !isPaymentActiveForWhatsappChannel) {
        return (
          <SubscribeModalWrapper
            isWhiteTheme={isWhiteTheme}
            channelDetails={channelDetails}
            closeModal={closeModal}
            hideTitleImage={hideTitleImage}
          >
            <PaymentDisabledModal />
          </SubscribeModalWrapper>
        );
      }

      if (showSubscriptionBundleModal && !(isCreator || isCoCreator) && !showAppDownloadModal) {
        return (
          <SubscribeModalWrapper
            isWhiteTheme={isWhiteTheme}
            channelDetails={channelDetails}
            closeModal={closeModal}
            hideTitleImage={hideTitleImage}
          >
            <SubscriptionBundleModal
              channelDetails={channelDetails}
              getUserChannelDetail={getUserChannelDetail}
              setShowThanksModal={setShowThanksModal}
              isSubscribed={isSubscribed}
              setHideTitleImage={setHideTitleImage}
              showAuthModal={showAuthModal}
              setShowAuthModal={setShowAuthModal}
              couponState={couponState}
              onCouponValidation={onCouponValidation}
              prepareCouponResponse={prepareCouponResponse}
              setReadyToPay={setReadyToPay}
              isDoingPayment={isDoingPayment}
              loadingMessage={loadingMessage}
              isLoadingApiResponse={isLoadingApiResponse}
              isWhiteTheme={isWhiteTheme}
              showTaxDetails={showTaxDetails}
            />
          </SubscribeModalWrapper>
        );
      }
    }

    if (!userData?.uid || !userData?.mobile) {
      return (
        <ModalWrapper closeModal={closeModal}>
          <AddMobileModal customConfig={customConfig} />
        </ModalWrapper>
      );
    }

    if (!userChannelDetail.hasOwnProperty("name") || isLoadingApiResponse) {
      return (
        <SubscribeModalWrapper
          isWhiteTheme={isWhiteTheme}
          channelDetails={channelDetails}
          closeModal={closeModal}
          hideTitleImage={hideTitleImage}
        >
          <div className={cls("flex flex-col items-center min-h-[200px]")}>
            <div className={cls(hasMultiplePlans ? "mt-10" : "mt-8")}>
              <Spinner size="lg" />
            </div>
            <p className={cls('mt-5 font-nunito', isWhiteTheme && 'text-[#16191b]')}>{loadingMessage}</p>
          </div>
        </SubscribeModalWrapper>
      );
    }

    // TODO: true ||
    if (showThanksModal || showTelegramModal || showWhatsappModal) {
      return (
        <SubscribeModalWrapper
          isWhiteTheme={isWhiteTheme}
          channelDetails={channelDetails}
          closeModal={closeModal}
          hideTitleImage={hideTitleImage}
        >
          {getThankYouModal()}
        </SubscribeModalWrapper>
      );
    }

    if (showMeetingDetailsModal) {
      return (
        <MeetingDetailsModal
          closeModal={closeModal}
          channelDetails={channelDetails}
          setModalOpen={setModalOpen}
          setShowThanksModal={setShowThanksModal}
        />
      );
    }

    if (
      (isSubscribed|| isCreator || isCoCreator) &&
      !isTelegramChannel &&
      !isWhatsappChannel
    ) {
      return (
        <SubscribeModalWrapper
          isWhiteTheme={isWhiteTheme}
          channelDetails={channelDetails}
          closeModal={closeModal}
          hideTitleImage={hideTitleImage}
        >
          <AppStoreModal
            isPrivate={isPrivate}
            channelDetails={channelDetails}
          />
        </SubscribeModalWrapper>
      );
    }
  }

  return (
    <Modal
      isOpen={modalOpen}
      closeModal={closeModal}
      afterLeave={afterSubscribeModalLeave}
    >
      {handleModalShowing()}
    </Modal>
  );
};

export default memo(JoinModals);
